import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Trash2 } from 'lucide-react';

import { FlexColumn } from 'src/components/flex';
import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import { useNotifications } from 'src/notifications';
import { isStandardErrorResponse } from 'src/api/common';
import {
    type IContact,
    type IProperty,
    resolvePropertyContactAssignments,
    useContacts,
    useProperties,
    useSetContactProperties,
    useSetPropertyContactsEnabled,
} from 'src/services/api';
import { contactDisplayName } from 'src/services/api/contacts/contact-display-name';

import styles from 'src/components/create-or-update-property-modal/PropertyContactsSection.module.css';
import modalStyles from 'src/components/create-or-update-property-modal/CreateOrUpdatePropertyModal.module.css';

interface Props {
    property: IProperty;
}

export default function PropertyContactsSection({ property }: Props) {
    const { addNotification } = useNotifications();
    const { data: contacts, isLoading } = useContacts();
    const { data: properties } = useProperties();
    const { mutateAsync: setContactProperties } = useSetContactProperties();
    const { mutateAsync: setPropertyContactsEnabled } = useSetPropertyContactsEnabled();

    // Draft association state keyed by contact ID. Differences from the
    // effective direct-or-inherited state count as unsaved changes.
    const [edits, setEdits] = useState<Record<number, boolean>>({});
    const [isAddingContact, setIsAddingContact] = useState(false);
    const [enabledEdit, setEnabledEdit] = useState<boolean>();
    const [isSaving, setIsSaving] = useState(false);

    const isContactLinked = (contact: IContact): boolean =>
        contact.property_ids.includes(property.id);

    // the switches reflect the property's effective contacts: with no own
    // links these are inherited from the nearest ancestor with any linked,
    // resolved locally from the cached contacts and properties lists
    const resolvedAssignments = contacts === undefined || properties === undefined ? [] :
        resolvePropertyContactAssignments(contacts, properties, property.id);
    const resolvedContactIds = contacts === undefined || properties === undefined ? undefined :
        new Set(resolvedAssignments.map(({ contact }) => contact.id));
    const isContactEffective = (contact: IContact): boolean =>
        resolvedContactIds === undefined ? isContactLinked(contact) : resolvedContactIds.has(contact.id);

    const isContactOn = (contact: IContact): boolean =>
        edits[contact.id] ?? isContactEffective(contact);

    const resolvedContactsEnabled = resolvedAssignments[0]?.enabled ?? property.is_contacts_invited;
    const areContactsEnabled = enabledEdit ?? resolvedContactsEnabled;

    // A child property inherits its contacts and invitation status from its
    // parent, so its contacts and the invitation switch cannot be edited here.
    const areContactsInherited = property.parent_property_id != null;

    const changedContacts = (contacts ?? []).filter((contact) =>
        edits[contact.id] !== undefined && edits[contact.id] !== isContactEffective(contact)
    );
    const hasUnsavedChanges = changedContacts.length > 0 || areContactsEnabled !== resolvedContactsEnabled;

    const applyChanges = async () => {
        if (!hasUnsavedChanges) {
            addNotification('Contacts are already up to date', 'success');
            return;
        }

        // Only materialize inherited contacts when the contact selection changes.
        // An enabled-only edit must preserve the property's inherited assignments.
        const updates = changedContacts.length > 0 ?
            (contacts ?? []).filter((contact) =>
                isContactOn(contact) !== isContactLinked(contact)
            ) :
            [];

        setIsSaving(true);
        try {
            await Promise.all(updates.map((contact) => {
                const newPropertyIds = isContactOn(contact) ?
                    [...contact.property_ids, property.id] :
                    contact.property_ids.filter((propertyId) => propertyId !== property.id);
                return setContactProperties({
                    id: contact.id,
                    property_ids: newPropertyIds,
                });
            }));
            await setPropertyContactsEnabled({
                propertyId: property.id,
                enabled: areContactsEnabled,
            });
            setEdits({});
            setEnabledEdit(undefined);
            addNotification('Contacts updated', 'success');
        } catch (error) {
            if (isStandardErrorResponse(error)) {
                addNotification(error.msg || 'Failed to update contacts', 'error');
            }
        } finally {
            setIsSaving(false);
        }
    };

    let content: React.ReactNode = null;
    if (contacts !== undefined) {
        const associatedContacts = contacts.filter(isContactOn);
        const unassociatedContacts = contacts.filter((contact) => !isContactOn(contact));
        content = (
            <>
                {!isLoading && contacts.length === 0 && (
                    <Alert severity="info" data-testid="property-contacts-empty-alert">
                        Your team does not have any contacts yet.
                        Create them with the Contacts button at the top of the properties page.
                    </Alert>
                )}
                {areContactsInherited && (
                    <Alert severity="info" data-testid="property-contacts-inherited-alert">
                        These contacts are inherited from a parent property.
                        Edit them on the parent to change this property&rsquo;s contacts.
                    </Alert>
                )}
                <FlexColumn rowGap={1}>
                    {associatedContacts.map((contact) => (
                        <div key={contact.id} className={styles.contactRow}>
                            <div className={styles.contactMain}>
                                <ProfileImageRounded userName={contactDisplayName(contact)} width={32} height={32} />
                                <div className={styles.contactInfo}>
                                    <Typography variant="body2">{contactDisplayName(contact)}</Typography>
                                    {contact.name && (
                                        <Typography variant="caption" color="text.secondary">{contact.email}</Typography>
                                    )}
                                </div>
                            </div>
                            <IconButton
                                onClick={() => setEdits((prev) => ({ ...prev, [contact.id]: false }))}
                                disabled={isSaving || areContactsInherited}
                                size="small"
                                aria-label={`Remove ${contactDisplayName(contact)}`}
                                data-testid={`remove-property-contact-${contact.id}`}
                            >
                                <Trash2 size={16} />
                            </IconButton>
                        </div>
                    ))}
                </FlexColumn>

                {isAddingContact && (
                    <Autocomplete
                        options={unassociatedContacts}
                        disabled={isSaving || areContactsInherited}
                        getOptionLabel={contactDisplayName}
                        onChange={(_event, contact) => {
                            if (contact) {
                                setEdits((prev) => ({ ...prev, [contact.id]: true }));
                                setIsAddingContact(false);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                autoFocus
                                label="Contact"
                                placeholder="Choose a contact"
                                size="small"
                            />
                        )}
                        data-testid="add-property-contact-picker"
                    />
                )}
                {!isAddingContact && (
                    <Button
                        type="button"
                        variant="outlined"
                        size="medium"
                        fullWidth
                        onClick={() => setIsAddingContact(true)}
                        disabled={isSaving || areContactsInherited || unassociatedContacts.length === 0}
                        className={styles.addContactButton}
                        data-testid="add-property-contact-button"
                    >
                        Add Contact
                    </Button>
                )}
                <div className={styles.actions}>
                    <LoadingButton
                        className={modalStyles.sectionSaveButton}
                        variant="contained"
                        size="small"
                        loading={isSaving}
                        disabled={!hasUnsavedChanges}
                        onClick={applyChanges}
                        data-testid="save-property-contacts-button"
                    >
                        Save
                    </LoadingButton>
                </div>
            </>
        );
    }

    return (
        <FlexColumn rowGap={2}>
            <div className={styles.titleColumn}>
                <div className={styles.titleRow}>
                    <div className={styles.titleAndSwitch}>
                        <Typography variant="body2">Send calendar invites to property&rsquo;s contacts</Typography>
                        <Switch
                            checked={areContactsEnabled}
                            onChange={(_event, checked) => setEnabledEdit(checked)}
                            disabled={isSaving || areContactsInherited}
                            size="small"
                            inputProps={{ 'aria-label': 'Send calendar invites to property’s contacts' }}
                            data-testid="property-contacts-enabled-switch"
                        />
                    </div>
                    {hasUnsavedChanges && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            data-testid="property-contacts-dirty-indicator"
                        >
                            Unsaved changes
                        </Typography>
                    )}
                </div>
            </div>
            {content}
        </FlexColumn>
    );
}
